import React, { useEffect, useRef, useState } from 'react';
import { Loader, Message } from 'rsuite';
import { Button, Modal } from '@nsi-advisory-services/components';
import { useExpenseAttachmentContent, useExpenseAttachments } from '../../hooks/useQueries';
import { buildExpenseReceiptPdf, buildReceiptFileName } from '../../utils/expenseReceiptPdf';

const triggerBlobDownload = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'expense-receipt.pdf';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default function ExpenseReceiptModal({ open, expense, onCancel, onClose }) {
  const expenseId = expense?.id;
  const attachmentsQuery = useExpenseAttachments(expenseId, {
    enabled: open && Boolean(expenseId),
  });
  const contentMutation = useExpenseAttachmentContent();

  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const generatedForRef = useRef(null);

  // Reset whenever the modal closes so a fresh PDF is built on the next open.
  useEffect(() => {
    if (open) return;
    generatedForRef.current = null;
    setPdfBlob(null);
    setError(null);
    setGenerating(false);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [open]);

  useEffect(() => {
    if (!open || !expense || !expenseId) return;
    if (attachmentsQuery.isLoading) return;
    if (attachmentsQuery.error) return;
    if (generatedForRef.current === expenseId) return;

    generatedForRef.current = expenseId;
    let cancelled = false;

    (async () => {
      setGenerating(true);
      setError(null);
      try {
        const rows = attachmentsQuery.data || [];
        const fetched = [];
        for (const row of rows) {
          const result = await contentMutation.mutateAsync({
            expenseId,
            attachmentId: row.id,
            download: true,
          });
          const bytes = new Uint8Array(await result.blob.arrayBuffer());
          fetched.push({
            fileName: row.file_name,
            contentType: result.contentType || row.content_type,
            bytes,
          });
        }

        const pdfBytes = await buildExpenseReceiptPdf(expense, fetched);
        if (cancelled) return;

        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfBlob(blob);
        setPdfUrl(url);
      } catch (err) {
        if (!cancelled) {
          setError(err);
          generatedForRef.current = null;
        }
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, expense, expenseId, attachmentsQuery.isLoading, attachmentsQuery.error, attachmentsQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const isBusy = attachmentsQuery.isLoading || generating;
  const loadError = error || attachmentsQuery.error;

  const handleDownload = () => {
    if (!pdfBlob) return;
    triggerBlobDownload(pdfBlob, buildReceiptFileName(expense));
  };

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <Modal.Header>
        <Modal.Title>Receipt Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: '78vh' }}>
        {loadError ? (
          <Message type="error" showIcon header="Could not generate receipt">
            {String(loadError?.message || loadError)}
          </Message>
        ) : isBusy || !pdfUrl ? (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ height: '76vh', gap: 12 }}
          >
            <Loader size="md" />
            <div className="text-muted">Generating receipt…</div>
          </div>
        ) : (
          <iframe
            title="Receipt preview"
            src={pdfUrl}
            style={{ width: '100%', height: '76vh', border: '1px solid var(--dm-border, #bfc8c9)', borderRadius: 8 }}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" icon="download" onClick={handleDownload} disabled={!pdfBlob}>
          Download PDF
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
